/**
 * SecondaryNav — role-aware horizontal tab strip rendered below the
 * Mi6BrandBand header on every /dashboards/* view.
 *
 * Each role gets a curated list of tabs that map to existing, registered
 * routes in App.tsx.  The active tab is highlighted with Mi6 navy.
 *
 * Palette: Navy #0B0B0F · Red #DC2626 · AIP bg #FAFAF7
 * No emojis, no exclamation points.
 *
 * Design: thin (40px) strip, white bg, hairline bottom border.
 * Active tab: black fill + white text pill.
 * Inactive tab: transparent, slate text, black hover.
 */

import { useEffect, useState } from "react";

// ─── Tab definitions per role ─────────────────────────────────────────────────

export type SecondaryNavRole =
  | "marketing"
  | "bdr"
  | "ae"
  | "management"
  | "executive"
  | "se"
  | "all";

interface NavTab {
  label: string;
  href: string;
}

const NAV_TABS: Record<SecondaryNavRole, NavTab[]> = {
  marketing: [
    { label: "Overview",       href: "/#/dashboards/marketing"   },
    { label: "Plan",           href: "/#/marketing/plan"          },
    { label: "Calendar",       href: "/#/marketing/calendar"      },
    { label: "Hooks",          href: "/#/marketing/hooks"         },
    { label: "Activity Log",   href: "/#/log/marketing"           },
    { label: "Question Bank",  href: "/#/question-bank"           },
    { label: "Templates",      href: "/#/templates"               },
    { label: "Opportunity Calculator", href: "/#/calculator"      },
    { label: "Account List",   href: "/#/"                        },
    { label: "Manual",         href: "/#/manuals/marketing"        },
  ],

  bdr: [
    { label: "Overview",       href: "/#/dashboards/bdr"          },
    { label: "Methodology",    href: "/#/methodology"              },
    { label: "Question Bank",  href: "/#/question-bank"           },
    { label: "Templates",      href: "/#/templates"               },
    { label: "Activity Log",   href: "/#/log/bdr"                 },
    { label: "Cadence",        href: "/#/cadence/bdr"             },
    { label: "Upload Leads",   href: "/#/upload-contacts"         },
    { label: "Opportunity Calculator", href: "/#/calculator"      },
    { label: "Manual",         href: "/#/manuals/bdr"              },
  ],

  ae: [
    { label: "Daily",          href: "/#/dashboards/daily"        },
    { label: "Overview",       href: "/#/dashboards/ae"           },
    { label: "Plan vs Actual", href: "/#/plan-vs-actual"          },
    { label: "Leaderboard",    href: "/#/leaderboard"             },
    { label: "Question Bank",  href: "/#/question-bank"           },
    { label: "Methodology",    href: "/#/methodology"              },
    { label: "Templates",      href: "/#/templates"               },
    { label: "Activity Log",   href: "/#/log/ae"                  },
    { label: "Cadence",        href: "/#/cadence/ae"              },
    { label: "Upload Leads",   href: "/#/upload-contacts"         },
    { label: "Opportunity Calculator", href: "/#/calculator"      },
    { label: "Manual",         href: "/#/manuals/ae"               },
  ],

  management: [
    { label: "Overview",        href: "/#/dashboards/management"   },
    { label: "Team",            href: "/#/team-dna"                },
    { label: "Sales Plan",      href: "/#/sales-plan"              },
    { label: "Leaderboard",     href: "/#/leaderboard"             },
    { label: "Plan vs Actual",  href: "/#/plan-vs-actual"          },
    { label: "Question Review", href: "/#/question-review"         },
    // W66.X1 — CP2-10 Audit is an internal Bryan-only tool; removed from manager nav.
    { label: "Methodology",     href: "/#/methodology"              },
    { label: "Question Bank",   href: "/#/question-bank"           },
    { label: "Opportunity DNA", href: "/#/opportunity-dna"         }, // W2-Alias: canonical
    { label: "Opportunity Calculator", href: "/#/calculator"       },
    { label: "Manual",          href: "/#/manuals/manager"         },
  ],

  executive: [
    { label: "Overview",        href: "/#/dashboards/executive"    },
    { label: "Sales Plan",      href: "/#/sales-plan"              },
    { label: "Leaderboard",     href: "/#/leaderboard"             },
    { label: "Plan vs Actual",  href: "/#/plan-vs-actual"          },
    { label: "Reporting",       href: "/#/reporting"               },
    { label: "Question Bank",   href: "/#/question-bank"           },
    { label: "Methodology",     href: "/#/methodology"              },
    { label: "Opportunity DNA", href: "/#/opportunity-dna"         }, // W2-Alias: canonical
    { label: "Opportunity Calculator", href: "/#/calculator"       },
    { label: "Manual",          href: "/#/manuals/executive"       },
  ],

  se: [
    { label: "Overview",       href: "/#/dashboards/se"            },
    { label: "Question Bank",  href: "/#/question-bank"            },
    { label: "Templates",      href: "/#/templates"                },
    { label: "Methodology",    href: "/#/methodology"              },
    { label: "Opportunity DNA", href: "/#/opportunity-dna"         }, // W2-Alias: canonical
    { label: "Opportunity Calculator", href: "/#/calculator"       },
    { label: "Manual",          href: "/#/manuals"                 },
  ],

  all: [
    { label: "Overview",       href: "/#/dashboards/all"           },
    { label: "Accounts",       href: "/#/"                         },
    { label: "Question Bank",  href: "/#/question-bank"            },
    { label: "Templates",      href: "/#/templates"                },
    { label: "Methodology",    href: "/#/methodology"              },
    { label: "Opportunity Calculator", href: "/#/calculator"       },
    { label: "Manuals",        href: "/#/manuals"                  },
  ],
};

// ─── Detect active tab from current hash ─────────────────────────────────────

function currentHash(): string {
  return window.location.hash || "#/";
}

function isActive(tabHref: string, hash: string): boolean {
  // Exact or prefix match (to handle e.g. /#/marketing/hooks/:slug)
  const tabPath = tabHref.replace(/^#/, "");
  const currentPath = hash.replace(/^#/, "");
  if (tabPath === "/#/" || tabPath === "/") {
    return currentPath === "/" || currentPath === "" || currentPath === "/#/";
  }
  return currentPath === tabPath || currentPath.startsWith(tabPath + "/");
}

// ─── Component ────────────────────────────────────────────────────────────────

interface SecondaryNavProps {
  role: SecondaryNavRole;
}

export default function SecondaryNav({ role }: SecondaryNavProps) {
  const tabs = NAV_TABS[role] ?? NAV_TABS["all"];

  // Track hash changes so active indicator updates on navigation
  const [hash, setHash] = useState(currentHash);
  useEffect(() => {
    function onHashChange() {
      setHash(currentHash());
    }
    window.addEventListener("hashchange", onHashChange);
    window.addEventListener("popstate", onHashChange);
    return () => {
      window.removeEventListener("hashchange", onHashChange);
      window.removeEventListener("popstate", onHashChange);
    };
  }, []);

  return (
    <nav
      aria-label="Section navigation"
      style={{
        background: "#FFFFFF",
        borderBottom: "1px solid #E5E5E7",
        // sits directly below the Mi6BrandBand (which is sticky top-0 z-50 h-16=64px)
        // and below the role-picker header (sticky top-14 z-40)
        // We attach it inside the role-picker header, so no extra sticky needed.
      }}
    >
      <div
        className="max-w-7xl mx-auto px-4 sm:px-6"
        style={{ overflowX: "auto", whiteSpace: "nowrap", scrollbarWidth: "none" }}
      >
        {/* Hide webkit scrollbar */}
        <style>{`.sec-nav-scroll::-webkit-scrollbar{display:none}`}</style>
        <div
          className="sec-nav-scroll flex items-center gap-0.5 py-1.5"
          style={{ overflowX: "auto", whiteSpace: "nowrap", scrollbarWidth: "none" }}
        >
          {tabs.map((tab) => {
            const active = isActive(tab.href, hash);
            return (
              <a
                key={tab.href}
                href={tab.href}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "5px 13px",
                  borderRadius: 9999,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  transition: "background 0.13s, color 0.13s",
                  background: active ? "#0B0B0F" : "transparent",
                  color: active ? "#FFFFFF" : "#525258",
                  border: active ? "1px solid #0B0B0F" : "1px solid transparent",
                }}
                onMouseEnter={(e) => {
                  if (!active) {
                    (e.currentTarget as HTMLAnchorElement).style.background = "#EFEFEE";
                    (e.currentTarget as HTMLAnchorElement).style.color = "#0B0B0F";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
                    (e.currentTarget as HTMLAnchorElement).style.color = "#525258";
                  }
                }}
              >
                {tab.label}
              </a>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
